import React, { useEffect, useRef, useCallback, useReducer } from 'react';
import _ from 'lodash';
import { getSelectedLanguage } from 'libs/context';
import { fetchMarkers, fetchSingleLocation } from './libs/fetchData';
import {
    createMap,
    extractBounds,
    createMarker,
    goToCurrentPosition,
    zoomInALittleBit,
    goToPosition,
    centerToMarker,
} from './libs/map';
import { toggleMarkerSelection, prepareLocationDetail, getMarkersToRemove, getMarkersToKeep } from './libs/markers';
import { defaultMarkerFilter } from './libs/getDefaultOptions';

import ToolBox from './ToolBox.js';
import LocationDetailSidebar from './DetailSidebar/component';
import LocationFilter from './LocationFilter/component';

import './LocationMap.scss';

const reducer = (state, { payload, type }) => {
    switch (type) {
        case 'setCurrentMarker': {
            let newState = {};

            if (Object.keys(state.currentMarker).length) {
                toggleMarkerSelection(state.currentMarker, false);
            }

            if (payload) {
                const newMarker = {
                    marker: payload.marker,
                    data: payload.data,
                    extendedData: payload.extendedData,
                };
                toggleMarkerSelection(newMarker, true);
                newState = { ...state, currentMarker: newMarker };
            } else {
                newState = { ...state, currentMarker: {} };
            }

            return newState;
        }
        case 'setAllMarkers': {
            const toRemove = getMarkersToRemove(payload, state.allMarkers);
            const newAllMarkers = getMarkersToKeep(state.allMarkers, toRemove);

            toRemove.forEach((item) => item.setMap(null));

            return {
                ...state,
                allMarkers: [...newAllMarkers, ...payload],
            };
        }
        case 'showCarousel': {
            return { ...state, showCarousel: payload };
        }
        case 'closeCarousel': {
            return { ...state, closeCarousel: payload };
        }
        case 'setSidebarStatus': {
            return { ...state, sidebarStatus: payload };
        }
        case 'setMap': {
            return { ...state, map: payload.map, google: payload.google };
        }
        case 'setFilter': {
            return { ...state, filter: payload };
        }
        case 'setPinMarker': {
            let pinMarker = state.pinMarker;

            if (pinMarker) {
                pinMarker.setPosition(payload);
            } else {
                pinMarker = new state.google.maps.Marker({
                    position: payload,
                    map: state.map,
                });
            }

            return { ...state, pinMarker };
        }
        case 'showFilter': {
            return { ...state, showFilter: payload };
        }
        default:
            return state;
    }
};
const initialState = {
    allMarkers: [],
    currentMarker: {},
    showFilter: false,
    filter: defaultMarkerFilter,
    pinMarker: null,
    map: null,
    google: null,
    showCarousel: true,
    closeCarousel: false,
    showSearch: false,
    sidebarStatus: null,
};

const goToMyPosition = (map) =>
    goToCurrentPosition(getSelectedLanguage()).then((pos) => {
        goToPosition(map, pos);
    });

const LocationMap = () => {
    const mapContainerRef = useRef(null);
    const toolBoxRef = useRef(null);
    const [state, dispatch] = useReducer(reducer, initialState);

    const fetchOnlyMarkers = useCallback(() => {
        const bounds = extractBounds(state.map);

        return bounds ? fetchMarkers(bounds, state.filter).then((data) => data) : [];
    }, [state.filter, state.map]);

    const setMarkerAsSelected = useCallback((marker, data, details) => {
        const rawData = _.extend({}, data, details);
        const extendedData = prepareLocationDetail(rawData);

        dispatch({
            type: 'setCurrentMarker',
            payload: { marker, data, extendedData },
        });
    }, []);

    const onSingleMarkerClick = useCallback(
        (marker, data) => {
            dispatch({ type: 'showCarousel', payload: false });
            dispatch({ type: 'setSidebarStatus', payload: 'loading' });

            centerToMarker(marker.map, marker);
            fetchSingleLocation(data.locationUid).then((details) => {
                setMarkerAsSelected(marker, data, details);
                dispatch({ type: 'setSidebarStatus', payload: 'open' });
            });
        },
        [setMarkerAsSelected],
    );

    const onClusterMarkerClick = useCallback((marker) => {
        zoomInALittleBit(marker.map, marker.getPosition());
    }, []);

    const onMapChange = useCallback(async () => {
        const data = await fetchOnlyMarkers();
        const fetchedMarkers = _.map(data, (marker) =>
            createMarker(state.map, marker, state.google, onSingleMarkerClick, onClusterMarkerClick),
        );

        dispatch({ type: 'setAllMarkers', payload: fetchedMarkers });
    }, [fetchOnlyMarkers, state.map, state.google, onSingleMarkerClick, onClusterMarkerClick]);

    useEffect(() => {
        if (!state.map || !state.google) {
            createMap(mapContainerRef.current, getSelectedLanguage()).then(({ map, google }) => {
                dispatch({ type: 'setMap', payload: { map, google } });

                map.controls[google.maps.ControlPosition.TOP_RIGHT].push(toolBoxRef.current);
                goToMyPosition(map);
            });
        }

        const mapEvents = state.google?.maps.event;

        if (state.google) {
            if (mapEvents.hasListeners(state.map, 'tilesloaded')) {
                mapEvents.clearListeners(state.map, 'tilesloaded');
            }

            mapEvents.addListener(state.map, 'tilesloaded', onMapChange);
            onMapChange();
        }
    }, [onMapChange, state.google, state.map]);

    const onSidebarTabClick = () => {
        dispatch({ type: 'showCarousel', payload: true });
        dispatch({ type: 'setSidebarStatus', payload: 'closed' });
        dispatch({ type: 'setCurrentMarker', payload: null });
    };

    const toggleFilterModal = (toggle) => {
        dispatch({ type: 'showFilter', payload: toggle });
    };

    const setFilter = (filter) => {
        dispatch({ type: 'setFilter', payload: filter });
        toggleFilterModal(false);
        onSidebarTabClick();
    };

    const setPinMarker = (coordinates) => {
        dispatch({ type: 'setPinMarker', payload: coordinates });
    };

    const onPlaceSelected = (data) => {
        goToPosition(state.map, data.coordinates);
        setPinMarker(data.coordinates);
    };

    const onMarkerSelected = (cp) => {
        goToPosition(state.map, {
            lat: cp.coordinates.latitude,
            lng: cp.coordinates.longitude,
        });
    };

    return (
        <div className="LocationMap">
            <LocationDetailSidebar
                status={state.sidebarStatus}
                data={state.currentMarker.extendedData}
                onTabClick={onSidebarTabClick}
            />
            <LocationFilter
                filter={state.filter}
                show={state.showFilter}
                onHide={toggleFilterModal.bind(null, false)}
                onFilterApply={setFilter}
                toggleFilterModal={toggleFilterModal}
            />
            <ToolBox
                ref={toolBoxRef}
                map={state.map}
                google={state.google}
                currentFilter={state.filter}
                onPlaceSelected={onPlaceSelected}
                onMarkerSelected={onMarkerSelected}
                onGoToMyPositionClick={() => goToMyPosition(state.map)}
                toggleFilterModal={toggleFilterModal}
            />
            <div className="LocationMap__map-container" ref={mapContainerRef} />
        </div>
    );
};

export default LocationMap;
